import React from 'react';
import { Modal, Form, Row, Col } from 'react-bootstrap';
import moment from 'moment';

// Services & Helpers
import GlobalStateService from 'services/GlobalStateService';
import ContractService from 'services/ContractService';
import BootboxHelper from 'helpers/BootboxHelper';

// Components
import Loader from 'components/common/Loader';
import DatePicker from 'components/common/DatePicker';

//-------------------------------------------------------------------------------------------------------------------

class ContractModal extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            isOpen: false
        };
    }

    show(opts) {
        return new Promise((resolve, reject) => {
            this.opts = opts;
            this.resolve = resolve;
            this.id = opts.id;
            this.instructorID = opts.instructorID;
            this.subAgencyID = opts.subAgencyID;
            this.loginInfo = GlobalStateService.getValue('loginInfo');

            this.setState({
                isOpen: true,
                isLoading: true,
                requestID: 0
            });
            this.load();
        });
    }

    close() {
        if (this.state.isChanged) {
            this.resolve();
        }
        this.setState({
            isOpen: false,
            isChanged: false
        });
    }

    async loadVersion(id) {
        this.id = id;
        this.load();
    }

    async load() {
        this.setState({ isLoading: true });
        const contract = await ContractService.get({
            id: this.id,
            instructorID: this.instructorID,
            subAgencyID: this.subAgencyID
        });
        let versions;
        if (this.id) {
            versions = await ContractService.listVersions({
                instructorID: contract.instructorID,
                subAgencyID: contract.subAgencyID
            });
        }
        this.setState({
            contract,
            versions,
            isLoading: false,
            isChanged: false,
            requestID: this.state.requestID + 1
        });
    }

    async confirmSendLink() {
        const { contract } = this.state;
        let msg;
        if (contract.dateSigned) {
            msg = 'This contract has already been signed. Send it again?';
        } else if (contract.dateSent) {
            msg = 'This contract has already been sent. Send it again?';
        } else {
            msg = 'Send contract?';
        }
        const confirm = await BootboxHelper.confirm(msg);
        if (confirm) {
            this.sendLink();
        }
    }

    async sendLink() {
        this.setState({ isLoading: true, isChanged: true });
        await ContractService.sendLinks([ this.state.contract.id ]);
        this.setState({ isLoading: false });
    }

    async confirmSendPDF() {
        const confirm = await BootboxHelper.confirm('Send the signed PDF via email?');
        if (confirm) {
            this.sendPDF();
        }
    }

    async sendPDF() {
        this.setState({ isLoading: true });
        await ContractService.sendPDF(this.state.contract.id);
        this.setState({ isLoading: false });
    }

    async regeneratePDF() {
        this.setState({ isLoading: true, isChanged: true });
        await ContractService.regeneratePDF(this.state.contract.id);
        this.setState({ isLoading: false });
    }

    async confirmCreateNewVersion() {
        const confirm = await BootboxHelper.confirm('This will create a new blank contract that must be signed. The current version will be archived. Continue?');
        if (confirm) {
            this.setState({ isLoading: true, isChanged: true });
            const { id } = await ContractService.createNewVersion(this.state.contract.id);
            this.id = id;
            this.load();
        }
    }

    updateFields(fields) {
        const contract = { ...this.state.contract };
        for (let fieldName in fields) {
            const value = fields[fieldName];
            contract[fieldName] = value;
        }
        this.setState({
            contract,
            isChanged: true
        });
    }

    async save() {
        this.setState({ isLoading: true });
        await ContractService.save(this.state.contract);
        this.setState({
            isLoading: false,
            isChanged: false,
            requestID: this.state.requestID + 1
        });
    }

    render() {
        const {
            isOpen
        } = this.state;

        if (!isOpen) {
            return null;
        }

        return (<>
            
            <Modal show className="contract-modal">
                {this.renderInner()}
            </Modal>

        </>);
    }

    renderInner() {
        const {
            isLoading,
            contract,
            versions,
            requestID
        } = this.state;

        if (isLoading) {
            return (
                <Modal.Body>
                    <Loader />
                </Modal.Body>
            );
        }

        const isLatest = !!versions && contract.id == versions[versions.length - 1].id;

        return (<>
            <Modal.Body>

                <Row>

                    <Col md="4">

                        {this.renderDetails()}
                        
                    </Col>

                    <Col md="8">

                        <iframe src={`/api/contract/get-pdf/${contract.id}?requestID=${requestID}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&messages=0&scrollbar=0`} />

                        {!!contract.uploadID &&
                            <button type="button" className="btn btn-secondary mt-1" onClick={() => this.regeneratePDF()}>
                                <span className="fa fa-arrows-rotate" />{' '}
                                Re-generate PDF
                            </button>
                        }

                        {!!contract.dateSigned && isLatest &&
                            <button type="button" className="btn btn-secondary mt-1" onClick={() => this.confirmCreateNewVersion()}>
                                <span className="fa-solid fa-file-signature" />{' '}
                                Create new version
                            </button>
                        }

                    </Col>

                </Row>

            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-secondary me-auto" onClick={() => this.close()}>
                    Close
                </button>
                {!contract.dateSigned &&
                    <button type="button" className="btn btn-primary ms-auto" onClick={() => this.confirmSendLink()}>
                        Send link to sign
                    </button>
                }
            </Modal.Footer>
        </>);
    }

    renderDetails() {
        const { contract, versions, isChanged } = this.state;

        return (<>

            <Form.Group className="mb-3">
                <Form.Label>Instructor / sub-agency</Form.Label>
                <p className="form-control-plaintext">
                    {contract.name}
                </p>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Recipients</Form.Label>
                <p className="form-control-plaintext">
                    {contract.emailAddresses.join(', ')}
                </p>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Version</Form.Label>
                {versions ?
                    <select
                        className="form-control"
                        value={contract.id || ''}
                        onChange={e => this.loadVersion(e.target.value)}
                    >
                        {versions.map(a =>
                            <option key={a.id} value={a.id}>
                                {a.version}
                                {a.id != contract.id ?
                                    (a.dateSigned ? ' (Signed ' + moment(a.dateSigned).format('DD/MM/YYYY') + ')' : ' (Not signed)') :
                                    ''
                                }
                            </option>
                        )}
                    </select> :
                    <p className="form-control-plaintext">
                        {contract.version}
                    </p>
                }
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Date Sent</Form.Label>
                <p className="form-control-plaintext">
                    {contract.dateLinkSent ? moment(contract.dateLinkSent).format('DD/MM/YYYY') : 'Not sent'}
                </p>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Date Signed</Form.Label>
                <p className="form-control-plaintext">
                    {contract.dateSigned ? moment(contract.dateSigned).format('DD/MM/YYYY') : 'Not signed'}
                    {contract.signedByUser && <>
                        <br />by {contract.signedByUser.firstName} {contract.signedByUser.lastName}
                        <br />
                        <button type="button" className="btn btn-secondary btn-sm mt-3" onClick={() => this.confirmSendPDF()}>
                            Send PDF
                        </button>
                    </>}
                </p>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Commencement Date</Form.Label>
                <DatePicker
                    className="form-control"
                    selected={contract.commencementDate ? new Date(contract.commencementDate) : null}
                    onChange={value => this.updateFields({ commencementDate: value ? moment(value).format('YYYY-MM-DD') : null })}
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Duration (months)</Form.Label>
                <input
                    type="number"
                    className="form-control"
                    value={contract.durationMonths || ''}
                    onChange={e => this.updateFields({ durationMonths: e.target.value })}
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Payment Terms</Form.Label>
                <input
                    type="number"
                    className="form-control"
                    value={contract.paymentTerms || ''}
                    onChange={e => this.updateFields({ paymentTerms: e.target.value })}
                />
            </Form.Group>

            {isChanged &&
                <button type="button" className="btn btn-primary" onClick={() => this.save()}>
                    Save Changes
                </button>
            }
        </>);
    }
    
}

export default ContractModal;